import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import {
  Calendar, MapPin, ChevronLeft, ExternalLink, Sparkles, Clock,
  Tag, Users, Lightbulb, Check, Home, ArrowRight, Star
} from 'lucide-react';
import { SiteHeader, SiteFooter, MobileBookBar } from '../components/SiteLayout';
import BookingDatePicker from '../components/BookingDatePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith('/api/') ? `${BACKEND_URL}${u}` : u);

const formatDate = (d) => {
  try { return new Date(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
};

const InfoPill = ({ icon: Icon, label, value, testId }) => (
  !value ? null : (
    <div className="flex items-center gap-2 bg-[#1a1f2e]/60 border border-[#C9A961]/20 px-3 py-2 rounded" data-testid={testId}>
      <Icon className="w-4 h-4 text-[#C9A961] shrink-0" strokeWidth={1.5} />
      <div className="text-[10px] uppercase tracking-wider text-[#C9A961]/70">{label}</div>
      <div className="text-sm text-[#E8E4DC]">{value}</div>
    </div>
  )
);

export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/content/events/by-slug/${slug}`)
      .then(r => setEvent(r.data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
    axios.get(`${API_URL}/content/events`).then(r => {
      const all = r.data || [];
      setRelated(all.filter(e => e.slug && e.slug !== slug).slice(0, 3));
    }).catch(() => {});
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
      <div className="text-[#C9A961] font-serif">Loading…</div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-[#0F1419] text-[#E8E4DC]">
      <SiteHeader />
      <div className="max-w-2xl mx-auto pt-32 pb-24 px-6 text-center">
        <h1 className="text-3xl font-serif text-[#E8E4DC] mb-4">Event not found</h1>
        <p className="text-sm text-[#E8E4DC]/60 mb-6">This event may have been removed or the link is incorrect.</p>
        <button onClick={() => navigate('/events')} className="inline-flex items-center gap-2 bg-[#C9A961] text-[#0F1419] px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#D4B76E]" data-testid="event-not-found-back">
          <ChevronLeft className="w-4 h-4" />Back to Events
        </button>
      </div>
      <SiteFooter />
    </div>
  );

  // Prefer hi-res hero variant; fall back to legacy `image` and upgrade low-res query if present.
  const upgradePex = (u) => {
    if (!u || typeof u !== 'string') return u;
    if (u.includes('images.pexels.com') && u.includes('w=1200')) {
      return u.replace('w=1200', 'w=1920');
    }
    return u;
  };
  const heroSrc = resolveImg(upgradePex(event.image_hero || event.image));
  const img = heroSrc;
  const thumb = resolveImg(event.image_thumb || event.image_hero || event.image);
  const hasExternal = event.external_link && event.external_link.trim();
  const title = event.seo_title || `${event.title} | Wentworth House Hotel Bath`;
  const desc = event.seo_description || event.description;
  const canonical = `https://www.wentworthhouse.co.uk/events/${event.slug}`;
  const highlights = Array.isArray(event.highlights) ? event.highlights.filter(Boolean) : [];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.title,
    "description": event.description,
    "startDate": event.date,
    "endDate": event.end_date || event.date,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": event.location ? {
      "@type": "Place", "name": event.location, "address": { "@type": "PostalAddress", "addressLocality": "Bath", "addressCountry": "GB" }
    } : undefined,
    "image": img || undefined,
    "url": canonical,
  };

  return (
    <div className="min-h-screen bg-[#0F1419] text-[#E8E4DC]" data-testid="event-detail-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc?.substring(0, 160) || ''} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={event.title} />
        <meta property="og:description" content={desc?.substring(0, 160) || ''} />
        {img && <meta property="og:image" content={img} />}
        {thumb && <meta name="twitter:image" content={thumb} />}
        <meta property="og:type" content="event" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <SiteHeader />

      {/* Cinematic Hero */}
      <section className="relative min-h-[75vh] pt-28 sm:pt-32 pb-14 px-4 sm:px-6 lg:px-10 overflow-hidden flex items-end">
        <div className="absolute inset-0 z-0">
          {img ? (
            <img
              src={img}
              alt={event.title}
              className="w-full h-full object-cover object-center"
              style={{ minHeight: '75vh' }}
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1a1f2e] to-[#0F1419]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1419]/40 via-[#0F1419]/60 to-[#0F1419]"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <Link to="/events" className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[#C9A961]/70 hover:text-[#C9A961] mb-6" data-testid="event-back-link">
            <ChevronLeft className="w-3 h-3" />Back to all events
          </Link>
          <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3 flex items-center gap-2">
            <Sparkles className="w-3 h-3" />Bath, UK · Local Event
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif text-[#E8E4DC] leading-tight mb-5 max-w-4xl" data-testid="event-title">{event.title}</h1>
          <div className="flex flex-wrap gap-3 mb-8">
            <InfoPill icon={Calendar} label="When" value={`${formatDate(event.date)}${event.end_date && event.end_date !== event.date ? ` — ${formatDate(event.end_date)}` : ''}`} testId="event-date" />
            {event.location && <InfoPill icon={MapPin} label="Where" value={event.location} testId="event-location" />}
            {event.duration && <InfoPill icon={Clock} label="Duration" value={event.duration} testId="event-duration" />}
            {event.price_hint && <InfoPill icon={Tag} label="Cost" value={event.price_hint} testId="event-price" />}
            {event.best_for && <InfoPill icon={Users} label="Best for" value={event.best_for} testId="event-bestfor" />}
          </div>
          {hasExternal && (
            <a
              href={event.external_link}
              target="_blank"
              rel="noopener noreferrer nofollow external"
              className="inline-flex items-center gap-2 bg-[#C9A961] text-[#0F1419] px-7 py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#D4B76E] transition-all"
              data-testid="event-external-link"
            >
              {event.external_link_label || 'Visit Official Site'}
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </section>

      {/* Intro + sidebar */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          {/* Main body */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <p className="text-lg sm:text-xl text-[#E8E4DC]/85 leading-relaxed font-light mb-6 first-letter:text-4xl first-letter:font-serif first-letter:text-[#C9A961] first-letter:float-left first-letter:leading-none first-letter:mr-2 first-letter:mt-1" data-testid="event-description">
                {event.description}
              </p>
              {event.long_description && (
                <div className="text-base text-[#E8E4DC]/70 leading-relaxed space-y-4 whitespace-pre-line" data-testid="event-long-description">
                  {event.long_description}
                </div>
              )}
            </div>

            {highlights.length > 0 && (
              <div className="border-l-2 border-[#C9A961] pl-6" data-testid="event-highlights">
                <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-4">Highlights</div>
                <ul className="space-y-3">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-base text-[#E8E4DC]/85">
                      <Check className="w-4 h-4 text-[#C9A961] shrink-0 mt-1.5" strokeWidth={2.5} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {event.what_to_expect && (
              <div data-testid="event-what-to-expect">
                <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3">What to expect</div>
                <p className="text-base text-[#E8E4DC]/75 leading-relaxed">{event.what_to_expect}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {event.insider_tip && (
              <div className="bg-[#C9A961]/10 border border-[#C9A961]/30 p-5" data-testid="event-insider-tip">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-[#C9A961]" strokeWidth={2} />
                  <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961]">Insider Tip</div>
                </div>
                <p className="text-sm text-[#E8E4DC]/90 leading-relaxed">{event.insider_tip}</p>
              </div>
            )}

            <div className="bg-gradient-to-br from-[#1a1f2e] to-[#0F1419] border border-[#C9A961]/20 p-6" data-testid="event-why-stay">
              <div className="flex items-center gap-2 mb-3">
                <Home className="w-4 h-4 text-[#C9A961]" strokeWidth={1.5} />
                <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961]">Perfect base</div>
              </div>
              <h3 className="text-lg font-serif text-[#E8E4DC] mb-3">Stay at Wentworth House</h3>
              <p className="text-sm text-[#E8E4DC]/70 leading-relaxed mb-4">
                {event.why_stay_here || 'A peaceful Georgian guest house just a 15-minute walk from Bath city centre. Free on-site parking and full English breakfast included.'}
              </p>
              <ul className="space-y-1.5 text-sm text-[#E8E4DC]/75 mb-5">
                <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#C9A961]" strokeWidth={3} />Free on-site parking</li>
                <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#C9A961]" strokeWidth={3} />Full English breakfast</li>
                <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#C9A961]" strokeWidth={3} />15 min walk to city centre</li>
                <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#C9A961]" strokeWidth={3} />Book direct = best rate</li>
              </ul>
              <Link to="/rooms" className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[#C9A961] hover:text-[#D4B76E] border-b border-[#C9A961]/30 hover:border-[#C9A961] pb-1 transition-all">
                View our rooms<ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {hasExternal && (
              <a
                href={event.external_link}
                target="_blank"
                rel="noopener noreferrer nofollow external"
                className="block bg-[#1a1f2e] border border-[#C9A961]/20 hover:border-[#C9A961]/50 p-5 transition-colors group"
              >
                <div className="text-xs tracking-[0.2em] uppercase text-[#C9A961]/70 mb-2">Official Event Info</div>
                <div className="flex items-center justify-between text-sm text-[#E8E4DC]">
                  <span>{event.external_link_label || 'Read more on the official site'}</span>
                  <ExternalLink className="w-4 h-4 text-[#C9A961] group-hover:translate-x-0.5 transition-transform" />
                </div>
              </a>
            )}
          </aside>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-10 bg-gradient-to-b from-[#0F1419] to-[#1a1f2e] border-t border-[#C9A961]/10">
        <div className="max-w-3xl mx-auto text-center">
          <Star className="w-6 h-6 text-[#C9A961] mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3">Book your stay</div>
          <h2 className="text-3xl sm:text-4xl font-serif text-[#E8E4DC] mb-3">Be here for {event.title.length > 40 ? 'this' : event.title}</h2>
          <p className="text-sm text-[#E8E4DC]/70 mb-8 max-w-xl mx-auto">Secure the best direct rate at Wentworth House — a warm Georgian welcome minutes from the action.</p>
          <div className="max-w-xl mx-auto">
            <BookingDatePicker />
          </div>
        </div>
      </section>

      {/* Related events */}
      {related.length > 0 && (
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-10 border-t border-[#C9A961]/10" data-testid="related-events">
          <div className="max-w-6xl mx-auto">
            <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3">More in Bath</div>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-8">Also happening nearby</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map(e => (
                <Link key={e.event_id} to={`/events/${e.slug}`} className="group border border-[#C9A961]/15 hover:border-[#C9A961]/40 bg-[#0F1419] overflow-hidden transition-colors">
                  {(e.image_thumb || e.image) && <div className="aspect-[4/3] overflow-hidden"><img src={resolveImg(e.image_thumb || e.image)} alt={e.title} loading="lazy" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" /></div>}
                  <div className="p-5">
                    <div className="text-xs text-[#C9A961]/80 mb-1">{formatDate(e.date)}</div>
                    <h3 className="text-lg font-serif text-[#E8E4DC] group-hover:text-[#C9A961] transition-colors">{e.title}</h3>
                    {e.location && <div className="text-xs text-[#E8E4DC]/50 mt-1">{e.location}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
      <MobileBookBar />
    </div>
  );
}
